// Canonical router-level route manifest. Drives App.tsx route generation,
// navConfig section composition, and Header trait lookup.
//
// Each entry describes one React Router pattern and its frontend-only
// properties. SEO metadata lives on shared/configs/pageRoutes instead — the
// two sources of truth sit at different granularities (router patterns here,
// expanded URLs there) and are joined at lookup time by path matching.

import { lazy, Suspense, type ReactElement } from 'react';

import DevPage from '@/devTools/DevPage';
import Draw from '@/pages/Draw';
import GameCoverChooser from '@/devTools/GameCoverChooser';
import BackgroundChooser from '@/devTools/BackgroundChooser';
import Campaigns from '@/pages/discover/Campaigns';
import Clips from '@/pages/discover/Clips';
import Music from '@/pages/discover/Music';
import FAQs from '@/pages/FAQs';
import Join from '@/pages/Join';
import Home from '@/pages/landing/Home';
import Scan from '@/pages/landing/Scan';
import GameLibrary from '@/pages/library/gameLibrary';
import LibraryPoppyPage from '@/pages/library/hostStyle/PoppyPage';
import AchievementsPage from '@/pages/library/achievements/AchievementsPage';
import Player from '@/pages/player/Player';
import { PlayingGameShell } from '@/pages/PlayingGameShell';
import Pricing from '@/pages/pricing/Pricing';
import Room from '@/pages/Room';
import WorldRoute from '@/pages/world/WorldRoute';
import { Skeleton } from '@/components/ui/skeleton';

const AssetPreview = lazy(
  () => import('@/devTools/assetPreview/AssetPreviewPage')
);
const PartyCentralPage = lazy(
  () => import('@/pages/partyCentral/PartyCentralPage')
);

const PartyCentralRoute = () => (
  <Suspense
    fallback={
      <div
        className="flex min-h-[calc(100svh-var(--app-header-h))] flex-col gap-6 bg-background p-4 pt-[calc(var(--app-header-h)+1rem)] sm:p-6 sm:pt-[calc(var(--app-header-h)+1.5rem)] md:p-8 md:pt-[calc(var(--app-header-h)+2rem)]"
        role="status"
        aria-live="polite"
        aria-label="Loading Party Central"
        aria-busy="true"
      >
        <Skeleton className="h-10 w-64 max-w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="min-h-96 w-full flex-1" />
      </div>
    }
  >
    <PartyCentralPage />
  </Suspense>
);

const AssetPreviewRoute = () => (
  <Suspense
    fallback={
      <div className="flex min-h-svh items-center justify-center bg-background text-sm text-muted-foreground">
        Loading asset preview...
      </div>
    }
  >
    <AssetPreview />
  </Suspense>
);

export type LayoutKey = 'header' | 'library' | 'bare';
export type AuthKey = 'public' | 'authenticated';
export type NavSectionKey = 'play-now' | 'discover' | 'more';

export type HeaderTraits = {
  liveNow?: boolean;
  transparent?: boolean;
};

export type NavPlacement = {
  section: NavSectionKey;
  label: string;
  order: number;
};

export type ContentRouteConfig =
  | {
      type: 'document';
      fallbackPath?: string;
    }
  | {
      type: 'collection';
      collectionId?: string;
      fallbackPath?: string;
    };

export type FrontendRouteEntry = {
  /** React Router path pattern (may include `:params` or `*` wildcard). */
  path: string;
  /** Target path when the entry is a redirect. Mutually exclusive with `element`/`layout`. */
  redirect?: string;
  element?: ReactElement;
  content?: ContentRouteConfig;
  layout?: LayoutKey;
  /** Defaults to 'public'. */
  auth?: AuthKey;
  /** Header visual/behavioral traits applied when this pattern is matched. */
  header?: HeaderTraits;
  /** Menu placement for desktop + mobile nav. Routes without this key are hidden from nav. */
  nav?: NavPlacement;
  /** Only registered when `import.meta.env.DEV`. */
  dev?: boolean;
};

export const routeManifest: FrontendRouteEntry[] = [
  // ========== Redirects ==========
  // Legacy paths that no longer host real pages. Naively funnel back to /library.
  { path: '/login', redirect: '/library?dialog=login' },
  { path: '/subscribe', redirect: '/library?dialog=login' },
  { path: '/email', redirect: '/library?dialog=login&step=email' },
  { path: '/email-verification', redirect: '/library?dialog=login&step=email' },
  { path: '/game-library', redirect: '/library' },
  { path: '/settings', redirect: '/library' },
  { path: '/for-teams', redirect: '/' },
  { path: '/for-streamers', redirect: '/' },
  { path: '/for-friends', redirect: '/' },
  { path: '/campaigns/new-year-new-games', redirect: '/' },
  { path: '/campaigns/march-madness', redirect: '/' },

  // ========== Header layout — public ==========
  {
    path: '/',
    element: <Home />,
    layout: 'header',
    header: { liveNow: true, transparent: true },
  },
  {
    path: '/join',
    element: <Join />,
    layout: 'header',
    header: { liveNow: true },
    nav: { section: 'play-now', label: 'Join Room', order: 2 },
  },
  {
    path: '/pricing',
    element: <Pricing />,
    layout: 'header',
    nav: { section: 'more', label: 'Pricing', order: 1 },
  },
  {
    path: '/faqs',
    element: <FAQs />,
    layout: 'header',
    nav: { section: 'more', label: 'Frequently Asked Questions', order: 2 },
  },
  {
    path: '/terms',
    content: { type: 'document', fallbackPath: '/' },
    layout: 'header',
    nav: { section: 'more', label: 'Terms & Conditions', order: 3 },
  },
  // Unlisted (no nav placement): public drawing board
  { path: '/draw', element: <Draw />, layout: 'header' },
  {
    path: '/scan',
    element: <Scan />,
    layout: 'header',
    header: { liveNow: true },
  },

  // Discover
  {
    path: '/discover',
    content: {
      type: 'collection',
      collectionId: 'collection:discover',
    },
    layout: 'header',
    header: { liveNow: true },
  },
  {
    path: '/discover/games',
    content: {
      type: 'collection',
      collectionId: 'collection:games',
    },
    layout: 'header',
    header: { liveNow: true },
    nav: { section: 'discover', label: 'Games', order: 1 },
  },
  {
    path: '/discover/games/:slug',
    content: { type: 'document', fallbackPath: '/discover/games' },
    layout: 'header',
    header: { liveNow: true },
  },
  {
    path: '/discover/music',
    element: <Music />,
    layout: 'header',
    header: { liveNow: true },
    nav: { section: 'discover', label: 'Music', order: 4 },
  },
  {
    path: '/discover/clips',
    element: <Clips />,
    layout: 'header',
    header: { liveNow: true },
    nav: { section: 'discover', label: 'Clips', order: 3 },
  },
  // Campaigns: exists but unlisted. Possible future relaunch.
  {
    path: '/discover/campaigns',
    element: <Campaigns />,
    layout: 'header',
    header: { liveNow: true },
  },
  {
    path: '/discover/features',
    content: {
      type: 'collection',
      collectionId: 'collection:features',
    },
    layout: 'header',
    header: { liveNow: true },
    nav: { section: 'discover', label: 'Features', order: 2 },
  },
  {
    path: '/discover/features/:slug',
    content: { type: 'document', fallbackPath: '/discover/features' },
    layout: 'header',
    header: { liveNow: true },
  },
  // Party Central
  {
    path: '/party-central',
    element: <PartyCentralRoute />,
    layout: 'header',
    nav: { section: 'more', label: 'Party Central', order: 4 },
  },
  {
    path: '/party-central/reports',
    content: {
      type: 'collection',
      collectionId: 'collection:reports',
      fallbackPath: '/party-central',
    },
    layout: 'header',
  },
  {
    path: '/party-central/reports/:slug',
    content: { type: 'document', fallbackPath: '/party-central/reports' },
    layout: 'header',
  },

  // Blog
  {
    path: '/blog',
    content: {
      type: 'collection',
      collectionId: 'collection:blog',
      fallbackPath: '/blog',
    },
    layout: 'header',
    header: { liveNow: true },
    nav: { section: 'discover', label: 'Blog', order: 5 },
  },
  {
    path: '/blog/category/:tag',
    content: {
      type: 'collection',
      collectionId: 'collection:blog',
      fallbackPath: '/blog',
    },
    layout: 'header',
    header: { liveNow: true },
  },
  {
    path: '/blog/:slug',
    content: { type: 'document', fallbackPath: '/blog' },
    layout: 'header',
    header: { liveNow: true },
  },

  // ========== Library layout ==========
  {
    path: '/library',
    element: <GameLibrary />,
    layout: 'library',
    nav: { section: 'play-now', label: 'Game Library', order: 1 },
  },
  { path: '/library/poppy', element: <LibraryPoppyPage />, layout: 'library' },
  {
    path: '/library/achievements',
    element: <AchievementsPage />,
    layout: 'library',
  },
  // Unreleased. No `nav` entry — that key feeds the marketing header menus,
  // which do not filter on access. WorldRoute holds the gate.
  { path: '/world', element: <WorldRoute />, layout: 'library' },

  // ========== Bare (no layout) ==========
  { path: '/room/:roomCode/player', element: <Player />, layout: 'bare' },
  {
    path: '/game/:roomCode/playing',
    element: <PlayingGameShell role="player" />,
    layout: 'bare',
  },
  {
    path: '/room/:roomCode',
    element: <Room />,
    layout: 'bare',
    auth: 'authenticated',
  },
  {
    path: '/game/:roomCode',
    element: <PlayingGameShell role="host" />,
    layout: 'bare',
    auth: 'authenticated',
  },

  // Dev tooling
  { path: '/dev', element: <DevPage />, layout: 'bare', dev: true },
  {
    path: '/dev/gen-image',
    element: <GameCoverChooser />,
    layout: 'bare',
    dev: true,
  },
  {
    path: '/dev/gen-background',
    element: <BackgroundChooser />,
    layout: 'bare',
    dev: true,
  },
  {
    path: '/dev/asset-preview',
    element: <AssetPreviewRoute />,
    layout: 'bare',
    dev: true,
  },
];
